import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  generateQuestions,
  saveQuestion,
  clearLastGenerated,
  selectIsGenerating,
  selectGenerateError,
  selectLastGenerated,
} from "../../../redux/slices/question-bank-slice";

const QUESTION_TYPES = [
  { value: "", label: "Mixed (model picks)" },
  { value: "VIDEO", label: "Video" },
  { value: "MCQ", label: "MCQ" },
  { value: "BEHAVIORAL", label: "Behavioral" },
  { value: "SKILL", label: "Skill" },
  { value: "CODING", label: "Coding" },
];
const DIFFICULTIES = [
  { value: "", label: "Mixed" },
  { value: "EASY", label: "Easy" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HARD", label: "Hard" },
  { value: "EXPERT", label: "Expert" },
];

export default function GenerateQuestionsModal({
  open,
  jobId,
  jobTitle,
  interviewRound,
  onClose,
  onAcceptedAll,
}) {
  const dispatch = useDispatch();
  const generating = useSelector(selectIsGenerating);
  const generateError = useSelector(selectGenerateError);
  const generated = useSelector(selectLastGenerated);

  const [count, setCount] = useState(5);
  const [questionType, setQuestionType] = useState("");
  const [competency, setCompetency] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    if (open) {
      setCount(5);
      setQuestionType("");
      setCompetency("");
      setDifficulty("");
      setAcceptError(null);
      setSavedCount(0);
      dispatch(clearLastGenerated());
    }
  }, [open, dispatch]);

  const handleGenerate = () => {
    if (generating) return;
    dispatch(
      generateQuestions({
        jobTitle: jobTitle || "this role",
        jobId,
        count,
        questionType: questionType || undefined,
        competency: competency || undefined,
        difficulty: difficulty || undefined,
        language: "en",
      }),
    );
  };

  const handleAcceptAll = async () => {
    if (!generated.length || accepting) return;
    setAccepting(true);
    setAcceptError(null);
    setSavedCount(0);
    for (let i = 0; i < generated.length; i++) {
      const g = generated[i];
      const timeSec = Math.max(15, Math.min(600, Number(g.suggestedTimeSec) || 90));
      const minutes = Math.floor(timeSec / 60);
      const seconds = timeSec % 60;
      const result = await dispatch(
        saveQuestion({
          jobId,
          interviewRound,
          question: {
            questionId: null,
            questionText: g.text || "",
            questionType: g.type || "VIDEO",
            responseType: g.type === "MCQ" ? "text" : "video",
            competency: g.competency || "",
            defaultLanguage: g.language || "en",
            difficulty: g.difficulty || "MEDIUM",
            responseTimeInMinutes: minutes,
            responseTimeInSeconds: seconds,
            tags: [],
            questionOptions: [],
            correctOption: "",
            lipSyncVideoLink: "",
            terminateInterview: false,
            isMoveToQuestionBank: true,
          },
        }),
      );
      if (saveQuestion.rejected.match(result)) {
        // Stop on first failure. Anything saved so far IS in the DB — surface
        // that count clearly so the user knows the modal can't just be re-clicked.
        const remaining = generated.length - i;
        setAcceptError(
          `Saved ${i} of ${generated.length}. ${remaining} not saved (${result.payload || "save failed"}). ` +
          `The saved ones are already in the list — close and try again for the rest.`,
        );
        setAccepting(false);
        // Refetch so the partial saves show up in the page list immediately.
        onAcceptedAll?.();
        return;
      }
      setSavedCount(i + 1);
    }
    setAccepting(false);
    onAcceptedAll?.();
    onClose?.();
  };

  const hasResults = generated.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={backdrop}
          />
          {/* Fixed full-viewport flex wrapper centers the dialog regardless of
              what framer-motion does to the dialog's own `transform`. The
              wrapper has pointer-events:none so clicks on the empty area pass
              through to the backdrop; the dialog re-enables pointer events
              on itself. */}
          <div style={centerWrap}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              style={dialog}
            >
            <div style={hdr}>
              <div style={hdrTitle}>✨ Generate questions with AI</div>
              <div style={hdrSub}>
                {jobTitle || "(no job title)"}
                {interviewRound ? ` · ${interviewRound}` : ""}
              </div>
            </div>

            <div style={body}>
              {!hasResults ? (
                <>
                  <div style={row}>
                    <div style={{ flex: 1 }}>
                      <div style={label}>Count (1–10)</div>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={count}
                        onChange={(e) =>
                          setCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))
                        }
                        style={input}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={label}>Question type</div>
                      <select
                        value={questionType}
                        onChange={(e) => setQuestionType(e.target.value)}
                        style={input}
                      >
                        {QUESTION_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={row}>
                    <div style={{ flex: 1 }}>
                      <div style={label}>Competency (optional)</div>
                      <input
                        type="text"
                        value={competency}
                        onChange={(e) => setCompetency(e.target.value)}
                        placeholder="e.g. React, System design"
                        style={input}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={label}>Difficulty</div>
                      <select
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                        style={input}
                      >
                        {DIFFICULTIES.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {generateError && (
                    <div style={errorBanner}>{generateError}</div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: "#475569", marginBottom: 8 }}>
                    {generated.length} question{generated.length === 1 ? "" : "s"} generated. Review and accept all, or close to discard.
                  </div>
                  <div style={resultList}>
                    {generated.map((g, i) => (
                      <div key={i} style={resultRow}>
                        <div style={resultText}>{g.text}</div>
                        <div style={resultMeta}>
                          {g.type && <span style={metaPill}>{g.type}</span>}
                          {g.difficulty && <span style={metaPill}>{g.difficulty}</span>}
                          {g.competency && <span style={metaPill}>{g.competency}</span>}
                          {g.suggestedTimeSec && (
                            <span style={metaPill}>{g.suggestedTimeSec}s</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {acceptError && <div style={errorBanner}>{acceptError}</div>}
                </>
              )}
            </div>

            <div style={footer}>
              <button onClick={onClose} style={ghostBtn} disabled={generating || accepting}>
                Close
              </button>
              {!hasResults ? (
                <button
                  onClick={handleGenerate}
                  disabled={generating || count < 1}
                  style={{
                    ...primaryBtn,
                    opacity: generating ? 0.5 : 1,
                    cursor: generating ? "not-allowed" : "pointer",
                  }}
                >
                  {generating ? "Generating…" : "Generate"}
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => dispatch(clearLastGenerated())}
                    style={ghostBtn}
                    disabled={accepting}
                  >
                    Generate different ones
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    disabled={accepting}
                    style={{
                      ...primaryBtn,
                      opacity: accepting ? 0.5 : 1,
                      cursor: accepting ? "not-allowed" : "pointer",
                    }}
                  >
                    {accepting
                      ? `Saving ${savedCount + 1} of ${generated.length}…`
                      : `Accept all ${generated.length}`}
                  </button>
                </div>
              )}
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.4)",
  zIndex: 90,
};
const centerWrap = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  // Empty area should pass clicks through to the backdrop so clicking outside
  // the dialog still closes the modal.
  pointerEvents: "none",
  padding: 16,
};
const dialog = {
  // Centered by the parent flex wrapper; no fixed positioning here so
  // framer-motion can own the `transform` string entirely for scale.
  width: 560,
  maxWidth: "100%",
  maxHeight: "85vh",
  background: "white",
  borderRadius: 10,
  boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  // Re-enable pointer events on the dialog itself (parent has them off).
  pointerEvents: "auto",
};
const hdr = { padding: "14px 18px", borderBottom: "1px solid #E2E8F0" };
const hdrTitle = { fontSize: 15, fontWeight: 700, color: "#0F172A" };
const hdrSub = { fontSize: 11, color: "#64748B", marginTop: 2 };
const body = { padding: "16px 18px", overflowY: "auto", flex: 1 };
const footer = {
  padding: "12px 18px",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  background: "#F8FAFC",
};
const row = { display: "flex", gap: 12, marginBottom: 12 };
const label = {
  fontSize: 10,
  color: "#64748B",
  marginBottom: 4,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const input = {
  width: "100%",
  padding: "7px 10px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};
const resultList = { display: "flex", flexDirection: "column", gap: 6 };
const resultRow = {
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  padding: "8px 10px",
};
const resultText = { fontSize: 12, color: "#0F172A", lineHeight: 1.45, marginBottom: 4 };
const resultMeta = { display: "flex", gap: 4, flexWrap: "wrap" };
const metaPill = {
  background: "#EEF2FF",
  color: "#4338CA",
  padding: "1px 7px",
  borderRadius: 100,
  fontSize: 10,
  fontWeight: 600,
};
const primaryBtn = {
  padding: "8px 16px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const ghostBtn = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const errorBanner = {
  background: "#FEE2E2",
  color: "#991B1B",
  padding: "8px 10px",
  borderRadius: 6,
  fontSize: 12,
  marginTop: 12,
};
